'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { refreshCreditsAction } from '@/dal/credit-ledger/actions';
import { getUser } from '@/dal/users/read';
import { deleteUserAction, updateUserAction, updateUserPreferencesAction } from '@/dal/users/actions';
import { NewUser, NewUserPreferences } from '@/lib/db/schema';
import { DEFAULT_LOCALE } from '@ui/lib/locale';
import { setLocale } from '@/i18n/request';
import { useTeam } from '@/hooks/useTeam';
import { isProd, parseSampleRate } from '@ui/lib/utils';

interface UserContextProps {
  user: UserWithoutMeta;
  creditsRemaining: number;
  updateUser: (data: NewUser) => Promise<void>;
  refreshCredits: () => Promise<void>;
  updatePreferences: (data: NewUserPreferences) => Promise<void>;
  deleteUser: () => Promise<void>;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

type UserWithoutMeta = NonNullable<Awaited<ReturnType<typeof getUser>>>;

export const UserProvider: React.FC<{
  initialUser: UserWithoutMeta;
  initialCredits: number;
  children: React.ReactNode;
}> = ({ initialUser, children, initialCredits }) => {
  const [user, setUser] = useState(initialUser);
  const [creditsRemaining, setCreditsRemaining] = useState(initialCredits);

  const { mutate: mutateTeam } = useTeam();

  useEffect(() => {
    datadogRum.setUser({
      id: user.id,
      name: user.fullName,
      email: user.email,
    });
    // Replay is off at init (sessionReplaySampleRate: 0). Start it here per DD_SESSION_REPLAY_SAMPLE_RATE:
    // 100 records every logged-in session (force), 1-99 starts without force, 0 never starts.
    const replayRate = parseSampleRate(process.env.NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE, 100);
    if (isProd && replayRate > 0) datadogRum.startSessionReplayRecording({ force: replayRate === 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCredits = async () => {
    try {
      const updatedCredits = await refreshCreditsAction();
      setCreditsRemaining(updatedCredits);
    } catch (error) {
      console.error('Failed to refresh credits:', error);
    }
  };

  const updateUser = async (data: NewUser) => {
    const result = await updateUserAction(data);
    setUser((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const updatePreferences = async (data: NewUserPreferences) => {
    const result = await updateUserPreferencesAction(data);
    setUser((prev) => ({
      ...prev,
      locale: result?.locale || DEFAULT_LOCALE,
      toneOfVoice: result?.toneOfVoice || null,
      length: result?.length || null,
    }));
    if (data.locale && user.locale !== data.locale) {
      await setLocale(data.locale);
      mutateTeam();
    }
  };

  const deleteUser = async () => {
    await deleteUserAction();
  };

  return (
    <UserContext.Provider
      value={{
        user,
        creditsRemaining,
        updateUser,
        refreshCredits,
        updatePreferences,
        deleteUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
